import React, { FC, AllHTMLAttributes, useState } from 'react';
import BaseModalComponent from '../BaseModal.component';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { StyledButton } from '../../Button.component';
import { Person } from '../../../redux/Candidate/Candidate.type';
import { useModalManager } from '../../../hooks/ModalManager.hook';
import SpinnerComponent from '../../Spinner.component';
import {
  getUrlBasedOnCurrentPath,
  mergeNewPersonWithExistingPerson,
} from '../Candidate/utils';
import { setDispatchModal } from '../../../redux/App/App.slice';
import { useAppDispatch } from '../../../hooks/useAppDispatcher.hook';
import CandidateContent from './components/CandidateContent';
import CandidateFooter from './components/CandidateFooter';
import { useAppSelector } from '../../../hooks/useAppSelector.hook';
import {
  getCandidateSelected,
  getIsEditCandidate,
  resetCandidateSelectedAction,
  setCandidateSelectedAction,
  setIsEditCandidateAction,
} from '../../../redux/Candidate/Candidate.slice';
import { getInternshipSelected } from '../../../redux/Internship/Internship.slice';
import { useInternship } from '../../../hooks/useInternship';
import ApiWrapper from '../../../hooks/useApi.hook';
import {
  mergePersonThunk,
  updatePersonThunk,
} from '../../../redux/Candidate/Candidate.thunk';
import { PersonUpdateFetch } from '../../../redux/Candidate/Experience/PersonExperience.type';
import { RoutesConstant } from '../../../constants/routes.constant';
import { useLocation, useNavigate } from 'react-router-dom';
import RecordPreNewModalComponent from '../Candidate/Record/RecordPreNewModal.component';

export type StatusDuplicateCandidate = 40001 | 40002;

export interface CandidateDuplicateModalProps
  extends AllHTMLAttributes<HTMLDivElement> {
  candidates?: Person[];
  status?: StatusDuplicateCandidate;
}

const StyledCandidateDuplicateBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 15px;

  ${StyledButton} {
    width: 100%;
  }
`;

const CandidateDuplicateModalComponent: FC<CandidateDuplicateModalProps> = ({
  candidates,
  status,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const selectedFormCandidate = useAppSelector(getCandidateSelected);
  const { t } = useTranslation();
  const [selectedCandidate, setSelectedCandidate] = useState(0);
  const { openModal, closeModal, closeAllModals } = useModalManager();
  const { savePersonResult, updatePersonResult } = useInternship();
  const selectedInternship = useAppSelector(getInternshipSelected);
  const isEditCandidate = useAppSelector(getIsEditCandidate);

  if (!candidates) {
    return <SpinnerComponent />;
  }

  const currentCandidate = candidates.find(
    candidate => candidate.id === selectedCandidate,
  );

  const redirectToCandidate = (id: number) => {
    const personUrl = getUrlBasedOnCurrentPath(location, id, [
      RoutesConstant.CANDIDATE,
      RoutesConstant.CANDIDATES_GROUP,
    ]);
    !!personUrl && navigate(personUrl);
  };

  const handleClose = () => {
    dispatch(setDispatchModal(undefined));
    dispatch(setIsEditCandidateAction(false));
    closeModal();
  };

  const editCandidateDetails = () => {
    dispatch(
      setCandidateSelectedAction({
        ...selectedFormCandidate.item,
        update: true,
      }),
    );
    ApiWrapper(
      updatePersonThunk(selectedFormCandidate.item as PersonUpdateFetch),
    ).then(closeAllModals);
  };

  const internshipCandidateAction = (isUnmergeDuplicate: boolean) => {
    const person = mergeNewPersonWithExistingPerson(
      selectedFormCandidate.item,
      currentCandidate,
    );
    isUnmergeDuplicate ? savePersonResult(person) : updatePersonResult(person);

    closeAllModals();
    dispatch(setDispatchModal(undefined));
    dispatch(resetCandidateSelectedAction(undefined));
    return;
  };

  const handleAction = (isUnmergeDuplicate: boolean) => {
    if (isUnmergeDuplicate && isEditCandidate) {
      editCandidateDetails();
      dispatch(setIsEditCandidateAction(false));
      return;
    }

    if (selectedInternship?.item) {
      internshipCandidateAction(isUnmergeDuplicate);
      return;
    }

    if (!currentCandidate?.id || !selectedFormCandidate.item.id) {
      openModal(
        RecordPreNewModalComponent,
        {
          mergePerson: mergeNewPersonWithExistingPerson(
            selectedFormCandidate.item,
            currentCandidate,
          ),
          isUnmergeDuplicate,
          isNewCandidate: true,
        },
        {
          replace: true,
        },
      );

      dispatch(setIsEditCandidateAction(false));
      return;
    }

    if (selectedFormCandidate.item.id) {
      ApiWrapper(
        mergePersonThunk(currentCandidate.id, selectedFormCandidate.item),
      ).then(() => {
        closeAllModals();
        dispatch(setIsEditCandidateAction(false));
        redirectToCandidate(currentCandidate.id);
      });
    }
  };

  return (
    <BaseModalComponent
      onCrossButtonClick={handleClose}
      header={t('personDuplicate.title')}
      footer={
        <CandidateFooter
          status={status}
          isDisable={!selectedCandidate}
          handleAction={handleAction}
          handleClose={handleClose}
        />
      }
    >
      <StyledCandidateDuplicateBody>
        <CandidateContent
          candidates={candidates}
          selectedCandidate={selectedCandidate}
          setSelectedCandidate={setSelectedCandidate}
        />
      </StyledCandidateDuplicateBody>
    </BaseModalComponent>
  );
};

export default CandidateDuplicateModalComponent;
